import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Home, HelpCircle, Bookmark, Wrench, ArrowRight } from "lucide-react";
import { searchSite, SearchResult } from "@/data/searchIndex";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const categoryIcons: Record<SearchResult["category"], React.ReactNode> = {
  page: <Home size={16} className="text-primary" />,
  section: <Bookmark size={16} className="text-accent" />,
  blog: <FileText size={16} className="text-green-500" />,
  faq: <HelpCircle size={16} className="text-orange-500" />,
  service: <Wrench size={16} className="text-blue-500" />,
};

const categoryLabels: Record<SearchResult["category"], string> = {
  page: "Page",
  section: "Section",
  blog: "Blog Post",
  faq: "FAQ",
  service: "Service",
};

const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    const searchResults = searchSite(query);
    setResults(searchResults);
    setSelectedIndex(0);
  }, [query]);

  const handleNavigate = (result: SearchResult) => {
    onOpenChange(false);
    
    // Handle hash navigation for sections on the same page
    if (result.url.startsWith("/#")) {
      const sectionId = result.url.replace("/#", "");
      if (window.location.pathname === "/") {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          element?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } else if (result.url.includes("#")) {
      const [path, hash] = result.url.split("#");
      navigate(path);
      setTimeout(() => {
        const element = document.getElementById(hash);
        element?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      navigate(result.url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleNavigate(results[selectedIndex]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Search the site</DialogTitle>
        </DialogHeader>
        
        {/* Search Input */}
        <div className="flex items-center border-b border-border px-4">
          <Search size={20} className="text-muted-foreground flex-shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, blog posts, FAQs..."
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-lg py-6 px-3"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="p-1 hover:bg-muted rounded transition-colors"
            >
              <X size={18} className="text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto">
          {query.length < 2 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Search size={40} className="mx-auto mb-4 opacity-30" />
              <p>Start typing to search...</p>
              <p className="text-sm mt-2">
                Search for pages, blog posts, FAQs, services, and more
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p className="text-lg font-medium mb-2">No results found</p>
              <p className="text-sm">
                Try different keywords or check your spelling
              </p>
            </div>
          ) : (
            <div className="py-2">
              {results.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleNavigate(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                    selectedIndex === index
                      ? "bg-primary/10"
                      : "hover:bg-muted/50"
                  }`}
                >
                  <div className="mt-1 flex-shrink-0">
                    {categoryIcons[result.category]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground truncate">
                        {result.title}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full flex-shrink-0">
                        {categoryLabels[result.category]}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {result.description}
                    </p>
                  </div>
                  <ArrowRight
                    size={16}
                    className={`mt-2 flex-shrink-0 transition-opacity ${
                      selectedIndex === index
                        ? "opacity-100 text-primary"
                        : "opacity-0"
                    }`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-4 py-3 bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px]">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px]">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px]">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-[10px]">Esc</kbd>
                <span className="ml-1">Close</span>
              </span>
            </div>
            <span>{results.length} results</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
